import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Textarea,
} from "@aircall/ds";
import { EllipsisVertical, UserRound } from "@aircall/react-icons";
import { format } from "date-fns";
import { type ComponentPropsWithoutRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown, { type ExtraProps } from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

import { formatPersonName, getInitials } from "@/helpers/string/string.helpers";
import { useCurrentUser } from "@/hooks/useCurrentUser/useCurrentUser";
import { useDeleteNote } from "@/hooks/useDeleteNote/useDeleteNote";
import { useUpdateNote } from "@/hooks/useUpdateNote/useUpdateNote";
import {
  NoteActionsProps,
  NotesListItemProps,
  UserAvatarProps,
} from "./NotesListItem.decl";

const MarkdownBoldText = ({
  children,
}: ComponentPropsWithoutRef<"h1"> & ExtraProps) => (
  <span className="text-xs font-medium" data-dd-privacy="mask">
    {children}
  </span>
);

const MarkdownText = ({
  children,
}: ComponentPropsWithoutRef<"p"> & ExtraProps) => (
  <span className="text-xs font-normal" data-dd-privacy="mask">
    {children}
  </span>
);

/** Renders note content with markdown + HTML support (XSS-safe via rehype-sanitize). */
function NoteContent({ content }: { content: string }) {
  return (
    <div className="break-words" data-dd-privacy="mask">
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          p: MarkdownText,
          h1: MarkdownBoldText,
          h2: MarkdownBoldText,
          h3: MarkdownBoldText,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function UserAvatar({ firstName, lastName }: UserAvatarProps) {
  if (!firstName || !lastName) {
    return (
      <Avatar size="sm">
        <AvatarFallback>
          <UserRound className="size-4" />
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar size="sm">
      <AvatarFallback>{getInitials(firstName, lastName)}</AvatarFallback>
    </Avatar>
  );
}

function NoteActions({ show, id, onDelete, onEdit }: NoteActionsProps) {
  const { t } = useTranslation();

  if (!show) {
    return <div className="w-7 h-7" />;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            data-test={`notes-list-item-button-${id}`}
            variant="ghost"
            size="icon-sm"
          />
        }
      >
        <EllipsisVertical className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-test={`notes-list-item-actions-${id}`}
        align="end"
        side="bottom"
      >
        <DropdownMenuItem
          data-test="notes-list-item-actions-edit"
          onClick={onEdit}
        >
          {t("edit")}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-test="notes-list-item-actions-delete"
          onClick={onDelete}
        >
          {t("delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function NotesListItem({
  id,
  content,
  createdAt,
  firstName,
  lastName,
  userId,
  isLoadingMutation,
}: NotesListItemProps) {
  const { t } = useTranslation();
  const [showTextarea, setShowTextarea] = useState(false);
  const [value, setValue] = useState(content);
  const { deleteNote } = useDeleteNote();
  const { updateNote, loading: isLoadingUpdate } = useUpdateNote();
  const { currentUser } = useCurrentUser();

  const loading = isLoadingUpdate || isLoadingMutation;

  const fullName =
    firstName && lastName ? formatPersonName(firstName, { lastName }) : "";
  const isNoteOwner = userId === Number(currentUser?.ID);

  const showActions = !loading && isNoteOwner;

  const handleCancelOnClick = () => {
    setShowTextarea(false);
    setValue(content);
  };
  const handleOnEdit = () => {
    setShowTextarea(true);
  };
  const handleOnDelete = () => {
    deleteNote({
      noteId: id,
    });
  };
  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };
  const handleUpdateOnClick = () => {
    updateNote({
      noteId: id,
      content: value,
    });
    setShowTextarea(false);
  };

  if (showTextarea) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <Textarea
          data-test="notes-edit-textarea"
          onChange={handleOnChange}
          value={value}
        />
        <div className="flex justify-end gap-2 w-full">
          <Button
            onClick={handleCancelOnClick}
            data-test="notes-list-item-actions-cancel"
            size="lg"
            variant="ghost"
          >
            {t("cancel")}
          </Button>
          <Button
            variant="secondary"
            onClick={handleUpdateOnClick}
            data-test="notes-list-item-actions-done"
            size="lg"
          >
            {t("done")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-test={`notes-list-item-${id}`}
      className="flex flex-col gap-1 w-full"
    >
      <div className="flex justify-between gap-1">
        <div className="flex gap-4 items-center">
          <div className="flex gap-1 items-center">
            <UserAvatar firstName={firstName} lastName={lastName} />
            <span className="text-xs font-medium">{fullName}</span>
          </div>
          <span className="text-xs font-normal text-muted-foreground">
            {format(new Date(createdAt), "MMM d - HH:mm")}
          </span>
        </div>
        <NoteActions
          onDelete={handleOnDelete}
          onEdit={handleOnEdit}
          show={showActions}
          id={id}
        />
      </div>
      <NoteContent content={content} />
    </div>
  );
}
